import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Flame, Dumbbell, Heart, Zap, Star, Users, Quote } from 'lucide-react';
import axios from 'axios';
import { useLang } from '@/context/LanguageContext';
import { ProgramArt } from '@/components/ProgramArt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  combat: Flame,
  strength: Dumbbell,
  mind_body: Heart,
  cardio: Zap,
  dance: Star,
  aqua: Heart,
  specialized: Users,
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' } }),
};

const HomePage = () => {
  const [programs, setPrograms] = useState([]);
  const [stories, setStories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { t, pick } = useLang();

  useEffect(() => {
    axios.get(`${API}/programs`).then(r => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/stories`).then(r => setStories(r.data)).catch(() => {});
    axios.get(`${API}/reviews`).then(r => setReviews(r.data)).catch(() => {});
  }, []);

  const featured = programs.slice(0, 6);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1608099/pexels-photo-1608099.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Hero"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r rtl:bg-gradient-to-l from-[#08080A] via-[#08080A]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-transparent to-[#08080A]/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20 w-full">
          <div className="max-w-2xl">
            <motion.p
              initial="hidden" animate="visible" variants={fadeUp}
              className="overline mb-6"
            >
              {t('home.overline')}
            </motion.p>
            <motion.h1
              initial="hidden" animate="visible" variants={fadeUp} custom={1}
              className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.1] mb-8"
            >
              {t('home.hero_1')} <br />
              <span className="font-bold gold-gradient-text">{t('home.hero_2')}</span>
            </motion.h1>
            <motion.p
              initial="hidden" animate="visible" variants={fadeUp} custom={2}
              className="text-lg text-[#A3A19C] leading-relaxed max-w-lg mb-10"
            >
              {t('home.hero_sub')}
            </motion.p>
            <motion.div
              initial="hidden" animate="visible" variants={fadeUp} custom={3}
              className="flex flex-wrap gap-4"
            >
              <Link to="/questionnaire" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-primary">
                {t('home.cta_primary')} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link to="/programs" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-secondary">
                {t('home.cta_secondary')} <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-5 h-8 rounded-full border border-[rgba(255,255,255,0.2)] flex items-start justify-center pt-1.5"
          >
            <div className="w-1 h-2 rounded-full bg-[#D4AF37]" />
          </motion.div>
        </div>
      </section>

      {/* Categories Strip */}
      <section className="py-16 border-b border-[rgba(255,255,255,0.06)]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap justify-center gap-6">
            {[
              { label: 'combat', icon: Flame },
              { label: 'strength', icon: Dumbbell },
              { label: 'cardio', icon: Zap },
              { label: 'mind_body', icon: Heart },
              { label: 'dance', icon: Star },
              { label: 'specialized', icon: Users },
            ].map((cat, i) => (
              <motion.div
                key={cat.label}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                className="flex items-center gap-3 px-6 py-3 rounded-full border border-[rgba(255,255,255,0.08)] bg-[#121215] hover:border-[rgba(212,175,55,0.3)] transition-border-color duration-300"
              >
                <cat.icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                <span className="text-sm text-[#A3A19C] font-medium">{t(`cat.${cat.label}`)}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Programs */}
      <section className="py-24 lg:py-32" data-testid="featured-programs">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="overline mb-4">{t('home.programs_overline')}</p>
              <h2 className="text-4xl lg:text-5xl font-light">
                {t('home.programs_1')} <span className="font-bold">{t('home.programs_2')}</span>
              </h2>
            </div>
            <Link to="/programs" className="hidden md:flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#E5C07B] transition-colors duration-300" data-testid="view-all-programs">
              {t('home.view_all')} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((program, i) => {
              const Icon = iconMap[program.category] || Star;
              return (
                <motion.div
                  key={program.id}
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                  className="group bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg overflow-hidden card-hover"
                  data-testid={`program-card-${i}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <ProgramArt program={program} alt={pick(program, 'name')} />
                    <div className="absolute bottom-4 start-4 flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                        program.intensity === 'high' ? 'bg-red-500/20 text-red-400' :
                        program.intensity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {t(`intensity.${program.intensity}`)}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      <h3 className="text-lg font-['Cormorant_Garamond'] font-bold text-[#F5F2EB]">{pick(program, 'name')}</h3>
                    </div>
                    <p className="text-sm text-[#A3A19C] leading-relaxed line-clamp-2">{pick(program, 'description')}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <Link to="/programs" className="md:hidden flex items-center justify-center gap-2 mt-8 text-sm text-[#D4AF37]">
            {t('home.view_all_programs')} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-[#121215] border-y border-[rgba(255,255,255,0.06)]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {[
              { value: '15+', label: t('home.stat_programs') },
              { value: '500+', label: t('home.stat_clients') },
              { value: '10+', label: t('home.stat_years') },
              { value: '3', label: t('home.stat_certs') },
            ].map((stat, i) => (
              <motion.div key={stat.label} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i} className="text-center lg:text-start">
                <p className="text-4xl lg:text-5xl font-['Cormorant_Garamond'] font-bold gold-gradient-text mb-2">{stat.value}</p>
                <p className="text-sm text-[#A3A19C]">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Transformation Stories */}
      {(stories.length > 0 || reviews.length > 0) && (
        <section className="py-24 lg:py-32" data-testid="stories-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <p className="overline mb-4">{t('home.stories_overline')}</p>
            <h2 className="text-4xl lg:text-5xl font-light mb-16">
              {t('home.stories_1')} <span className="font-bold">{t('home.stories_2')}</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {stories.map((story, i) => (
                <motion.div
                  key={story.id}
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                  className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-8 card-hover"
                  data-testid={`story-card-${i}`}
                >
                  <Quote className="w-6 h-6 text-[#D4AF37] mb-6" strokeWidth={1.5} />
                  <p className="text-sm text-[#A3A19C] leading-relaxed mb-4 italic">"{pick(story, 'before_text')}"</p>
                  <div className="w-8 h-px bg-[#D4AF37] mb-4" />
                  <p className="text-sm text-[#F5F2EB] leading-relaxed mb-6">"{pick(story, 'after_text')}"</p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[#E2B4BD]">{pick(story, 'name')}</p>
                    {story.program && <span className="text-xs text-[#A3A19C] px-3 py-1 rounded-full border border-[rgba(255,255,255,0.08)]">{pick(story, 'program')}</span>}
                  </div>
                </motion.div>
              ))}
              {reviews.map((review, i) => (
                <motion.div
                  key={review.id}
                  initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}
                  className="bg-[#121215] border border-[rgba(255,255,255,0.08)] rounded-lg p-8 card-hover flex flex-col"
                  data-testid={`review-card-${i}`}
                >
                  <Quote className="w-6 h-6 text-[#D4AF37] mb-6" strokeWidth={1.5} />
                  <p className="text-sm text-[#F5F2EB] leading-relaxed mb-6 flex-1" dir="auto">"{review.text}"</p>
                  <div className="w-8 h-px bg-[#D4AF37] mb-4" />
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[#E2B4BD]" dir="auto">{review.user_name}</p>
                    <span className="text-xs text-[#A3A19C] px-3 py-1 rounded-full border border-[rgba(255,255,255,0.08)]">{t('rev.client_review')}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#121215] border-t border-[rgba(255,255,255,0.06)]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="overline mb-6">{t('home.cta_overline')}</p>
            <h2 className="text-4xl lg:text-6xl font-light mb-6 max-w-3xl mx-auto">
              {t('home.cta_1')} <span className="font-bold gold-gradient-text">{t('home.cta_2')}</span>
            </h2>
            <p className="text-[#A3A19C] max-w-lg mx-auto mb-10">
              {t('home.cta_sub')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/questionnaire" className="btn-primary inline-flex items-center gap-2" data-testid="cta-assessment">
                {t('home.cta_assessment')} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link to="/contact" className="btn-secondary" data-testid="cta-contact">
                {t('home.cta_contact')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-16 border-t border-[rgba(255,255,255,0.06)]" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="overline text-center mb-8">{t('home.partners')}</p>
          <div className="flex flex-wrap items-center justify-center gap-12">
            <span className="text-sm text-[#A3A19C] tracking-[0.15em] uppercase font-medium">{t('cert.ereps')}</span>
            <span className="hidden md:block w-px h-4 bg-[rgba(255,255,255,0.1)]" />
            <span className="text-sm text-[#A3A19C] tracking-[0.15em] uppercase font-medium">{t('cert.ifa')}</span>
            <span className="hidden md:block w-px h-4 bg-[rgba(255,255,255,0.1)]" />
            <span className="text-sm text-[#A3A19C] tracking-[0.15em] uppercase font-medium">{t('cert.saudi_full')}</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
